import { PencilSimple, TrashSimple } from "@phosphor-icons/react";
import { Button } from "antd";
import { Helmet } from "react-helmet";

import { useUpdateURLSearchParams } from "@/hooks/useQuery";
import useSearchParams from "@/hooks/useSearchParams";
import { useLoadCustomerFields } from "@/queries/customerFields/useLoadCustomerField";
import { useLoadCustomerFieldsTypes } from "@/queries/customerFields/useLoadCustomerFieldsTypes";
import { TableZentake } from "@/ui/atoms/Table/TableZentake";
import Tooltip from "@/ui/atoms/Tooltip";
import Icons from "@/ui/Icons";
import SearchBar from "@/ui/SearchBar";

import TabBody from "../TabBody";
import { AddNewCustomFieldsModal } from "./CustomFieldsModal/AddNewCustomFieldsModal";
import { DeleteCustomFieldsModal } from "./CustomFieldsModal/DeleteCustomFieldsModal";
import { EditCustomFieldsModal } from "./CustomFieldsModal/EditCustomFieldsModal";

export const CustomFieldsTab = () => {
  const { data: customerFields, isLoading: isLoadingCustomerFields } =
    useLoadCustomerFields();
  const { data: customerFieldsTypes, isLoading: isLoadingCustomerFieldsTypes } =
    useLoadCustomerFieldsTypes();
  const updateParams = useUpdateURLSearchParams();
  const searchParams = useSearchParams();

  const search = searchParams.get("search")?.toLocaleLowerCase() || "";
  const addNewCustomField = searchParams.get("addNew") || "";
  const editCustomField = searchParams.get("editId") || "";
  const deleteCustomField = searchParams.get("deleteId") || "";

  const filteredCustomerFields = customerFields?.filter(
    (item) =>
      item.label.toLowerCase().includes(search) ||
      item.type.toLowerCase().includes(search),
  );

  const isLoading = isLoadingCustomerFields || isLoadingCustomerFieldsTypes;

  return (
    <>
      <Helmet>
        <title>Custom Fields - Settings/Zentake</title>
      </Helmet>
      <TabBody>
        <div className="flex flex-col w-full pt-2 gap-6">
          <div className="w-full grid grid-cols-[1fr_176px] gap-3">
            <SearchBar
              className="flex-1"
              onChange={(value) => updateParams(["search", String(value)])}
              placeholder="Search"
              value={search}
            />
            <Button
              onClick={() => updateParams(["addNew", "true"])}
              type="primary"
            >
              <Icons.Plus className="text-white" />
              Custom Field
            </Button>
          </div>
          <TableZentake.Root>
            <TableZentake.Head
              columns={["Label", "Type", "Show in Client Mapping", ""]}
            />
            <TableZentake.Body>
              {isLoading ? (
                <TableZentake.Row>
                  <TableZentake.Column colSpan={5}>
                    Loading...
                  </TableZentake.Column>
                </TableZentake.Row>
              ) : !filteredCustomerFields || !filteredCustomerFields.length ? (
                <TableZentake.Row>
                  <TableZentake.Column colSpan={5}>
                    No custom fields
                  </TableZentake.Column>
                </TableZentake.Row>
              ) : (
                filteredCustomerFields.map((field) => {
                  const getType = customerFieldsTypes?.find(
                    (item) => item.value === field.type,
                  );
                  return (
                    <TableZentake.Row key={field.id}>
                      <TableZentake.Column>{field.label}</TableZentake.Column>
                      <TableZentake.Column className="w-80">
                        {getType?.label}
                      </TableZentake.Column>
                      <TableZentake.Column className="w-56">
                        <div className="flex items-center w-full justify-center font-bold">
                          {field.show_in_client_mapping ? "Yes" : "No"}
                        </div>
                      </TableZentake.Column>
                      <TableZentake.Column className="w-48">
                        <div className="w-full flex justify-center gap-4">
                          <Tooltip mouseEnterDelay={0.35} title="Edit">
                            <Button
                              className="w-[fit-content] !px-4"
                              onClick={() => updateParams(["editId", field.id])}
                            >
                              <PencilSimple className="text-xl" />
                            </Button>
                          </Tooltip>
                          <Tooltip mouseEnterDelay={0.35} title="Delete">
                            <Button
                              className="w-[fit-content] !px-4"
                              onClick={() =>
                                updateParams(["deleteId", field.id])
                              }
                            >
                              <TrashSimple className="text-xl" />
                            </Button>
                          </Tooltip>
                        </div>
                      </TableZentake.Column>
                    </TableZentake.Row>
                  );
                })
              )}
            </TableZentake.Body>
          </TableZentake.Root>
        </div>
      </TabBody>
      {addNewCustomField ? <AddNewCustomFieldsModal /> : null}
      {editCustomField ? <EditCustomFieldsModal /> : null}
      {deleteCustomField ? <DeleteCustomFieldsModal /> : null}
    </>
  );
};
